import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import {
  getEmailConfigStatus, getEmailDomains, listMailboxes, createMailbox, setMailboxPaused,
  listMessages, type EmailAddress, type EmailMessageSummary,
} from '@/api/email';
import CreateMailboxModal from '@/components/email/CreateMailboxModal';
import MessageView from '@/components/email/MessageView';

function isPaused(a: EmailAddress): boolean {
  return a.status === 'paused';
}

// The feed can only be narrowed to a mailbox client-side: the upstream `source`
// field is the ingest connector, not the recipient address, so we match on the
// message's recipients instead.
function matchesMailbox(m: EmailMessageSummary, addr: string): boolean {
  const a = addr.toLowerCase();
  if (m.recipient_addresses?.some((r) => (r || '').toLowerCase() === a)) return true;
  return (m.to || '').toLowerCase().includes(a);
}

export default function Email() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [openMessageId, setOpenMessageId] = useState<string | null>(null);

  const config = useQuery({ queryKey: ['email', 'config'], queryFn: getEmailConfigStatus });
  const domains = useQuery({
    queryKey: ['email', 'domains'],
    queryFn: getEmailDomains,
    enabled: !!config.data?.configured,
  });
  const mailboxes = useQuery({
    queryKey: ['email', 'addresses'],
    queryFn: listMailboxes,
    enabled: !!config.data?.configured,
  });
  const messages = useQuery({
    queryKey: ['email', 'messages'],
    queryFn: () => listMessages({ limit: 50 }),
    enabled: !!config.data?.configured,
    refetchInterval: 20000,
  });
  const shownMessages = selected
    ? (messages.data || []).filter((m) => matchesMailbox(m, selected))
    : messages.data || [];

  const create = useMutation({
    mutationFn: createMailbox,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email', 'addresses'] }),
  });
  const pause = useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) => setMailboxPaused(id, paused),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email', 'addresses'] }),
  });

  if (config.isLoading) {
    return <div className="p-6 text-content-subtle">Loading…</div>;
  }

  if (!config.data?.configured) {
    return (
      <div className="p-6">
        <h1 className="mb-2 text-xl font-semibold text-content">Email</h1>
        <p className="text-content-subtle">
          Email API is not configured. Set the base URL and token in{' '}
          <a href="/settings" className="text-primary underline">Settings</a>.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <aside className="w-80 shrink-0 border-r border-border p-4">
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-lg font-semibold text-content">Mailboxes</h1>
          <EditOnly><Button size="sm" onClick={() => setShowCreate(true)}>New</Button></EditOnly>
        </div>
        {mailboxes.isLoading && <p className="text-sm text-content-subtle">Loading…</p>}
        {mailboxes.data?.length === 0 && (
          <p className="text-sm text-content-subtle">No mailboxes yet.</p>
        )}
        <ul className="space-y-1">
          {(mailboxes.data || []).map((a) => {
            const key = a.id || a.email_address;
            const sel = selected === a.email_address;
            return (
              <li key={key}>
                <div
                  className={`flex items-center justify-between rounded-md px-3 py-2 ${
                    sel ? 'bg-surface-muted' : ''
                  }`}
                >
                  <button
                    className="min-w-0 flex-1 text-left"
                    onClick={() => setSelected(selected === a.email_address ? null : a.email_address)}
                  >
                    <span className="block truncate text-sm text-content">{a.email_address}</span>
                    {a.description && (
                      <span className="block truncate text-xs text-content-subtle">{a.description}</span>
                    )}
                  </button>
                  <EditOnly>
                    <button
                      onClick={() => a.id && pause.mutate({ id: a.id, paused: !isPaused(a) })}
                      disabled={!a.id || pause.isPending}
                      className="ml-2 shrink-0 rounded px-2 py-1 text-xs text-content-subtle hover:text-content-muted disabled:opacity-40"
                    >
                      {isPaused(a) ? 'Resume' : 'Pause'}
                    </button>
                  </EditOnly>
                </div>
              </li>
            );
          })}
        </ul>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="text-lg font-semibold text-content">
            {selected ? `Inbox — ${selected}` : 'All messages'}
          </h2>
          <Button size="sm" variant="secondary" onClick={() => messages.refetch()}>
            Refresh
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">
          {messages.isLoading && <p className="p-4 text-content-subtle">Loading…</p>}
          {messages.isError && <p className="p-4 text-danger">Failed to load messages.</p>}
          {!messages.isLoading && !messages.isError && shownMessages.length === 0 && (
            <p className="p-4 text-content-subtle">No messages.</p>
          )}
          <ul className="divide-y divide-border">
            {shownMessages.map((msg: EmailMessageSummary) => (
              <li key={msg.id}>
                <button
                  onClick={() => setOpenMessageId(msg.id)}
                  className="block w-full px-4 py-3 text-left hover:bg-surface-muted"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-content">{msg.from || '—'}</span>
                    {(msg.attachments?.length ?? 0) > 0 && (
                      <span className="text-xs text-content-subtle">📎</span>
                    )}
                  </div>
                  <span className="block truncate text-sm text-content-muted">
                    {msg.subject || '(no subject)'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {showCreate && (
        <CreateMailboxModal
          domains={domains.data || []}
          onClose={() => setShowCreate(false)}
          onCreate={(input) => create.mutateAsync(input).then(() => undefined)}
        />
      )}

      {openMessageId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fade-in"
          onClick={() => setOpenMessageId(null)}
        >
          <div
            className="h-[85vh] w-full max-w-3xl overflow-hidden rounded-lg bg-surface shadow-xl animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <MessageView id={openMessageId} onClose={() => setOpenMessageId(null)} />
          </div>
        </div>
      )}
    </div>
  );
}
